/**
 * Creation and edition of a made-to-measure model with its light, dark and
 * (shirts) base pictures.
 */
import { useState, type SubmitEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  DMS_FITS,
  DMS_PRODUCTLINE,
  DMS_PRODUCTLINES,
} from '@/shared/constants';
import { ApiError } from '@/shared/api/client';
import { Dialog } from '@/shared/ui/Dialog';
import { Select } from '@/shared/ui/Select';
import { optionsOf } from '@/shared/ui/select-options';
import { PictureField } from '@/shared/ui/PictureField';
import { usePicture } from '@/shared/ui/use-picture';
import { createDmsItem, updateDmsItem, syncDmsItemPicture } from '../api';
import { sewingPatternsQueryOptions } from '../sewing-patterns/api';
import {
  formToModelData,
  MODEL_PICTURE_SUFFIX,
  MODEL_PICTURE_TYPES,
  modelDataSchema,
  modelFormValues,
  modelPictureUrl,
  validateModelForm,
  type DmsModel,
  type ModelData,
  type ModelFormErrors,
  type ModelFormValues,
  type ModelPictureType,
} from './model-form';
import { kebabCase } from '@/shared/lib/kebab-case';

export interface ModelDialogProps {
  item: DmsModel | null;
  /** Product line selected in the tree, preset on a new model. */
  productLine: string;
  onClose: (result: 'Confirm' | 'Cancel') => void;
}

const apiMessage = (error: unknown, fallback: string): string =>
  error instanceof ApiError && typeof error.body === 'string' && error.body
    ? error.body
    : fallback;

export function ModelDialog({
  item,
  productLine,
  onClose,
}: Readonly<ModelDialogProps>) {
  const creating = !item;
  const [values, setValues] = useState<ModelFormValues>(() =>
    modelFormValues(item, productLine),
  );
  // The referential, or nothing when it fails.
  const { data: sewingPatterns = [] } = useQuery(sewingPatternsQueryOptions());
  // The suit validators only apply once the product line select changes (see migration.md).
  const [suitRules, setSuitRules] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<ModelFormErrors>({});
  const [dirty, setDirty] = useState<Set<keyof ModelFormValues>>(
    () => new Set(),
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const pictures = {
    light: usePicture(),
    dark: usePicture(),
    base: usePicture(),
  };

  const set = <K extends keyof ModelFormValues>(
    key: K,
    value: ModelFormValues[K],
  ) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setDirty((prev) => new Set(prev).add(key));
  };
  const liveErrors = validateModelForm(values, suitRules);
  const invalidClass = (key: keyof ModelFormValues) =>
    dirty.has(key) && liveErrors[key] ? 'invalid' : '';
  const fieldError = (key: keyof ModelFormValues) => submitted && errors[key];
  const isSuit = values.productLine === DMS_PRODUCTLINE.SUIT;
  const isShirt = values.productLine === DMS_PRODUCTLINE.SHIRT;

  const handlePictures = async (
    id: string,
    code: string,
  ): Promise<Partial<ModelData>> => {
    const results = await Promise.all(
      MODEL_PICTURE_TYPES.filter((type) => pictures[type].touched).map(
        async (type) => {
          const url = await syncDmsItemPicture(
            id,
            type,
            `${code}${MODEL_PICTURE_SUFFIX[type]}`,
            pictures[type].file,
          );
          return { [`${type}Picture`]: url };
        },
      ),
    );
    return Object.assign({}, ...results) as Partial<ModelData>;
  };

  const save = useMutation({
    mutationFn: async () => {
      const data = formToModelData(values, item, Date.now());
      if (creating) {
        let created: DmsModel;
        try {
          created = await createDmsItem(
            { type: 'model', data },
            modelDataSchema,
          );
        } catch (error) {
          throw new Error(apiMessage(error, 'Failed to create item'));
        }
        try {
          const pictureData = await handlePictures(
            created.id,
            String(created.data?.model ?? ''),
          );
          await updateDmsItem(
            created.id,
            {
              data: { ...created.data, ...pictureData },
            },
            modelDataSchema,
          );
        } catch (error) {
          throw new Error('Failed to upload pictures', { cause: error });
        }
        return;
      }
      let pictureData: Partial<ModelData>;
      try {
        pictureData = await handlePictures(item.id, values.model);
      } catch (error) {
        throw new Error('Failed to upload pictures', { cause: error });
      }
      try {
        await updateDmsItem(
          item.id,
          {
            type: 'model',
            data: { ...data, ...pictureData },
          },
          modelDataSchema,
        );
      } catch (error) {
        throw new Error(apiMessage(error, 'Failed to update item'));
      }
    },
    onSuccess: () => onClose('Confirm'),
    onError: (error: Error) => setErrorMessage(error.message),
  });

  const onSubmit = (event: SubmitEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setErrorMessage(null);
    const nextErrors = validateModelForm(values, suitRules);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setErrorMessage('Please fix the errors');
      return;
    }
    save.mutate();
  };

  const testId = (key: string) => `mtm-model-form-${kebabCase(key)}`;

  const text = (
    key: keyof Pick<
      ModelFormValues,
      | 'model'
      | 'modelStyleCodePart'
      | 'jacketModel'
      | 'sizes'
      | 'provalineas'
      | 'description'
      | 'pantsModel'
    >,
    label: string,
    placeholder: string,
    message: string,
  ) => (
    <div className="mb-2">
      <label className="field-label" htmlFor={`model-${key}`}>
        {label}
      </label>
      <input
        id={`model-${key}`}
        data-testid={testId(key)}
        className={`field ${invalidClass(key)}`}
        name={key}
        placeholder={placeholder}
        value={values[key]}
        onChange={(event) => set(key, event.target.value)}
      />
      <small hidden={!fieldError(key)} data-testid={`${testId(key)}-error`}>
        {message}
      </small>
    </div>
  );

  const checkbox = (
    key: 'availablePermanent' | 'eveningOffer' | 'availableVillaDior',
    label: string,
  ) => (
    <div className="styled-input-square styled-input-single">
      <input
        type="checkbox"
        id={`model-${key}`}
        name={key}
        data-testid={testId(key)}
        checked={values[key]}
        onChange={(event) => set(key, event.target.checked)}
      />
      <label htmlFor={`model-${key}`}>{label}</label>
    </div>
  );

  const picture = (type: ModelPictureType, label: string) => (
    <PictureField
      id={`model-${type}Picture`}
      name={`${type}Picture`}
      testId={testId(`${type}Picture`)}
      label={label}
      picture={pictures[type]}
      serverUrl={modelPictureUrl(item, type)}
      labelStyle={type === 'light' ? { backgroundColor: '#1C1F2A' } : undefined}
    />
  );

  return (
    <Dialog
      title={creating ? 'Add a model' : 'Edit a model'}
      width="1200px"
      onClose={() => onClose('Cancel')}
      testId="mtm-model-dialog"
    >
      <form className="form popin-form" onSubmit={onSubmit} noValidate>
        <div className="bs-row">
          <div className="col-span-4">
            <div className="mb-2">
              <label className="field-label" htmlFor="model-productLine">
                Product Line
              </label>
              <div
                className={`field field-selector ${invalidClass('productLine')}`}
              >
                <Select
                  id="model-productLine"
                  name="productLine"
                  testId={testId('productLine')}
                  placeholder="--Select product line--"
                  options={DMS_PRODUCTLINES}
                  value={values.productLine}
                  onChange={(line) => {
                    set('productLine', line);
                    setSuitRules(line === DMS_PRODUCTLINE.SUIT);
                  }}
                />
              </div>
              <small
                hidden={!fieldError('productLine')}
                data-testid={`${testId('productLine')}-error`}
              >
                Wrong product line
              </small>
            </div>
            {text('model', 'Model Name', 'Model Name', 'Wrong model name')}
            {text(
              'modelStyleCodePart',
              'Model Code',
              'Model Code',
              'Wrong model code',
            )}
            <div className="mb-2">
              <label className="field-label" htmlFor="model-fit">
                Fit
              </label>
              <div className="field field-selector">
                <Select
                  id="model-fit"
                  name="fit"
                  testId={testId('fit')}
                  placeholder="--Select fit--"
                  options={optionsOf(DMS_FITS)}
                  value={values.fit}
                  onChange={(fit) => set('fit', fit)}
                />
              </div>
            </div>
            {isSuit &&
              text(
                'jacketModel',
                'Jacket Model Associated',
                'Jacket Model Associated',
                'Wrong jacket model',
              )}
            <div className="mb-1">
              <span className="field-label">Availability</span>
            </div>
            <div className="bs-row">
              <div className="col-span-6">
                {checkbox('availablePermanent', 'Permanent')}
              </div>
              <div className="col-span-6">
                {checkbox('eveningOffer', 'Evening Offer')}
              </div>
            </div>
            <div className="bs-row">
              <div className="col-span-6">
                {checkbox('availableVillaDior', 'Villa Dior')}
              </div>
            </div>
            {errorMessage && (
              <div
                className="alert mb-0"
                role="alert"
                data-testid="mtm-model-form-error"
              >
                <span className="error-msg">{errorMessage}</span>
              </div>
            )}
          </div>

          <div className="col-span-4">
            {text('sizes', 'Sizes', 'Sizes', 'Wrong sizes')}
            {text(
              'provalineas',
              'Provalineas',
              'Provalineas',
              'Wrong provalineas',
            )}
            {text(
              'description',
              'Description',
              'Description',
              'Wrong description',
            )}
            {isSuit &&
              text(
                'pantsModel',
                'Pants Model Associated',
                'Pants Model Associated',
                'Wrong pants model',
              )}
            <div className="mb-2">
              <label className="field-label" htmlFor="model-sewingPatterns">
                Sewing Pattern
              </label>
              <div className="field field-selector">
                <Select
                  multiple
                  id="model-sewingPatterns"
                  name="sewingPatternObjects"
                  testId="mtm-model-form-sewing-patterns"
                  placeholder="--Select sewing patterns--"
                  hideSelected
                  options={sewingPatterns.map((pattern) => ({
                    value: pattern.id,
                    label: pattern.name,
                  }))}
                  value={values.sewingPatternObjects.map(
                    (pattern) => pattern.id,
                  )}
                  onChange={(ids) =>
                    set(
                      'sewingPatternObjects',
                      ids.flatMap((id) => {
                        const pattern = sewingPatterns.find((p) => p.id === id);
                        return pattern ? [pattern] : [];
                      }),
                    )
                  }
                />
              </div>
            </div>
            <div className="mt-6 flex justify-center">
              <button
                className="button button-form"
                type="submit"
                data-testid="mtm-model-form-save-button"
                disabled={save.isPending}
              >
                Save
              </button>
            </div>
          </div>

          <div className="col-span-4">
            {picture('light', 'Picture 1: Light')}
            {picture('dark', 'Picture 2: Dark')}
            {isShirt && picture('base', 'Picture 3: Model Basis')}
          </div>
        </div>
      </form>
    </Dialog>
  );
}
